import Calendar from '@/components/calendar/Calendar';
import { Button } from '@/components/common/Button';
import { Carousel, CarouselContent, CarouselItem } from '@/components/common/Carousel';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/common/Form';
import { Input } from '@/components/common/Input';
import { Label } from '@/components/common/Label';
import { Switch } from '@/components/common/Switch';
import { Textarea } from '@/components/common/Textarea';
import SearchInput from '@/components/general/SearchInput';
import StepTimeSelect from '@/components/general/StepTimeSelect';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { loadFilePreview, loadURLPreview } from '@/utils/browsers';
import { checkImageSupportFormat, combineDateAndTime } from '@/utils/utils';
import { yupResolver } from '@hookform/resolvers/yup';
import { ImagePlusIcon, PlusIcon, Trash2Icon, X, XIcon } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import StarRating from './StarRating';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/common/Popover';
import { flowResult } from 'mobx';
import { toastify } from '@/utils/toastify';

const validateSchema = yup.object().shape({
    nameRewiewerSeedingComment: yup.string().required('form_error_validate.required'),
    incognito: yup.boolean(),
    dateReview: yup.date().nullable(),
    timeReview: yup.string().required(),
    serviceQuality: yup
        .number()
        .required('form_error_validate.required')
        .min(1, 'form_error_validate.required'),
    comment: yup
        .string()
        .max(2000)
        .nullable(),
    images: yup
        .array()
        .of(yup.mixed<string | File>().required('form_error_validate.required'))
        .checkFileImagesOfArrayExt('form_error_validate.shop_image_upload_format')
        .max(5, 'form_error_validate.post_review_image_upload_limit')
        .nullable()
});

type FormData = yup.InferType<typeof validateSchema>;

interface Shops {
    id: number,
    name: string
}

interface EditSeedingCommentProps {
    onSuccess?: () => void,
    shopId?: any
}
export default observer(function EditSeedingComment({ onSuccess, shopId }: EditSeedingCommentProps) {
    const { t } = useTranslation();

    const {
        modalStore: { showAlertModal, hideModal },
        reviewStore: { getReviewDetail, editSeedingComments }
    } = useStore();

    const [shop, setShop] = useState<any>();

    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onChange',
        defaultValues: {
            nameRewiewerSeedingComment: '',
            incognito: false,
            dateReview: new Date(),
            timeReview: '00:00',
            serviceQuality: 5,
            comment: null,
            images: []
        }
    });

    const watchImages = form.watch('images');
    const watchToDateReview = form.watch('dateReview');

    useEffect(() => {
        if (Array.isArray(watchImages) && watchImages.length > 0) {
            if (watchImages instanceof Array) {
                watchImages.forEach((image, index) => {
                    if (image instanceof File) {
                        loadFilePreview(image, `preview-image-${index}`);
                    } else {
                        loadURLPreview(image, `preview-image-${index}`);
                    }
                });
            }
        }
    }, [watchImages]);

    const onFetchData = async () => {
        if (shopId) {
            const iShop = await flowResult(getReviewDetail(shopId));
            setShop(iShop?.data);
        }
    };

    useEffect(() => {
        onFetchData();
    }, []);

    useEffect(() => {
        if (shop) {
            // Chuyển đổi createdAt từ UTC sang UTC+8
            const createdAtUTC = new Date(shop.createdAt); // Tạo đối tượng Date từ ISO string
            const createdAtUTC8 = new Date(createdAtUTC.getTime() + 7 * 60 * 60 * 1000); // Thêm 7 giờ (7 * 60 * 60 * 1000 ms)

            // Lấy ngày và giờ từ createdAtUTC8
            const dateReview = createdAtUTC;
            const timeReview = createdAtUTC8.toISOString().slice(11, 16); // Lấy giờ phút từ UTC+8 (HH:mm)

            // Xét lại dữ liệu cho form
            form.reset({
                nameRewiewerSeedingComment: shop?.nameRewiewerSeedingComment || '',
                incognito: shop.incognito || false,
                dateReview: dateReview,
                timeReview: timeReview,
                serviceQuality: shop.serviceQuality || 5,
                comment: shop.comment || null,
                images: shop.images || []
            });

            // Hiển thị preview cho các hình ảnh
            if (shop.images && shop.images.length > 0) {
                shop.images.forEach((item, index) => loadURLPreview(item, `preview-image-${index}`));
            }
        }
    }, [shop]);

    const onSubmit = async (data: FormData) => {
        const { nameRewiewerSeedingComment, dateReview, timeReview, incognito, serviceQuality, comment, images } = data;
        const combinedDateTime = combineDateAndTime(dateReview, timeReview);

        // edit review
        const imageFiles: File[] = [];
        const imageStrings: string[] = [];
        if (images) {
            images.forEach((image: string | File) => {
                if (image instanceof File) {
                    imageFiles.push(image);
                } else {
                    imageStrings.push(image);
                }
            });
        }

        if (shopId) {
            const res = await flowResult(editSeedingComments({
                nameRewiewerSeedingComment,
                dateReview: combinedDateTime,
                incognito,
                serviceQuality,
                comment,
                images: imageStrings,
                imageFiles,
                id: shopId
            }));
            if (res) {
                toastify('success', t('messages.update_success'));
                hideModal();
            }
        }
        onSuccess && onSuccess();
    };

    const submit = async (data: FormData) => {
        console.log('xxx');
    };

    const handleImageFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files) {
            const existingImages = watchImages || [];
            const totalImages = existingImages.length + files.length;
            if (totalImages > 5) {
                showAlertModal({
                    type: 'error',
                    content: t('sentences.maximum_review_image')
                });
                return;
            }

            if (checkImageSupportFormat(files)) {
                form.setValue('images', [...(files instanceof FileList ? Array.from(files) : []), ...existingImages], { shouldValidate: true });
            } else {
                showAlertModal({
                    type: 'error',
                    content: t('form_error_validate.shop_image_upload_format')
                });
            }
            e.target.value = '';
        }
    };

    const handleRomoveImg = (indexImg: number) => {
        if (Array.isArray(watchImages) && watchImages.length > 0) {
            form.setValue('images', [
                ...watchImages.slice(0, indexImg),
                ...watchImages.slice(indexImg + 1)
            ], { shouldValidate: true });
        }
    };

    const handleDisableTime = (date: Date, isToDate: boolean) => {
        const limitDate = new Date();
        return isToDate ? date < limitDate : date > limitDate;
    };

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(submit)} className='my-4'>
                <div className='mx-2 my-1 p-2 rounded-sm border border-[#e9e9e9] max-h-[calc(100vh-300px)] overflow-auto'>
                    <div>
                        <div className=''>
                            <FormItem>
                                <Label>
                                    {t('words_title.seeding_cmt_shopname', { index: '' })}
                                </Label>
                                <div className='mt-1'>{shop?.shop?.name}</div>
                            </FormItem>
                        </div>
                    </div>
                    <div className='flex mt-2 relative'>
                        <div className='w-1/2'>
                            <FormField
                                control={form.control}
                                name='nameRewiewerSeedingComment'
                                render={({ field, fieldState }) => (
                                    <FormItem>
                                        <Label required>
                                            {t('words_title.seeding_cmt_reviewer', { index: '' })}
                                        </Label>
                                        <Input
                                            className={fieldState.error ? 'border-red-500' : ''}
                                            value={field.value ?? ''}
                                            maxLength={255}
                                            onChange={e => field.onChange(e.target.value)}
                                        />
                                        <FormMessage className='w-5/6' />
                                    </FormItem>
                                )}
                            />
                        </div>
                        <div className='flex flex-wrap items-center absolute right-0 top-[28px]'>
                            <Label className='label-1 leading-[18px] mr-2'>{t('words_title.seeding_cmt_hide_fullname')}</Label>
                            <FormField
                                control={form.control}
                                name='incognito'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormControl>
                                            <Switch
                                                checked={field.value}
                                                onCheckedChange={(checked) => {
                                                    field.onChange(checked);
                                                }}
                                                className='mt-1 '
                                            />
                                        </FormControl>
                                    </FormItem>
                                )}
                            />
                        </div>
                    </div>

                    <div className='mt-3 flex w-full'>
                        <div className='w-1/2'>
                            <FormLabel className='inline-block mb-1'>{t('words_title.seeding_cmt_review_date')}</FormLabel>
                            <FormField
                                control={form.control}
                                name='dateReview'
                                render={({ field }) => (
                                    <FormItem className='w-full'>
                                        <FormControl>
                                            <Calendar
                                                mode='single'
                                                selected={field.value}
                                                onSelect={field.onChange}
                                                disabled={date => handleDisableTime(date, false)}
                                                useIconRemove={false}
                                                className='w-full'
                                                required
                                            />
                                        </FormControl>
                                    </FormItem>
                                )}
                            />
                        </div>
                        <div className='grow ml-2'>
                            <StepTimeSelect className='mt-[18px]' title='' name='timeReview' control={form.control} />
                        </div>
                    </div>

                    <div className='my-2 flex flex-row h-[40px] items-center'>
                        <Label className='label-1 leading-[40px] mr-3' required>{t('words_title.seeding_cmt_rate')}</Label>
                        <FormField
                            control={form.control}
                            name='serviceQuality'
                            render={({ field }) => (
                                <FormItem>
                                    <FormControl>
                                        <StarRating
                                            value={field.value}
                                            onChange={newValue => field.onChange(newValue)}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>

                    <FormField
                        control={form.control}
                        name='comment'
                        render={({ field }) => (
                            <FormItem>
                                <Label>{t('words_title.seeding_cmt_comment')}</Label>
                                <Textarea
                                    className='mt-[0.375rem] resize-none h-[133px]'
                                    value={field.value ?? ''}
                                    maxLength={2000}
                                    onChange={e => field.onChange(e.target.value)}
                                />
                            </FormItem>
                        )}
                    />

                    <div className='mt-[1rem]'>
                        <FormField
                            control={form.control}
                            name='images'
                            render={({ field: { onChange, value, ...rest } }) => (
                                <>
                                    <FormItem>
                                        <FormControl>
                                            <Input
                                                id='images'
                                                type='file'
                                                onChange={event => handleImageFileChange(event)}
                                                className='hidden'
                                                multiple={true}
                                                {...rest}
                                            />
                                        </FormControl>
                                        <Label htmlFor='images' className='flex items-center justify-center cursor-pointer border border-input rounded-lg py-3'>
                                            <ImagePlusIcon className='text-active h-6 w-6 mr-2' />
                                            <span className='text-placeholder-1 font-medium text-[0.9375rem] leading-4'>
                                                {t('button.add_image')}
                                            </span>
                                        </Label>
                                        <FormMessage />
                                    </FormItem>
                                    <Carousel
                                        opts={{
                                            align: 'start'
                                        }}
                                        className='w-full my-4'
                                    >
                                        <CarouselContent>
                                            {
                                                Array.isArray(watchImages) && watchImages.length > 0 &&
                                                Array.from(watchImages).map((_, indexImg) => (
                                                    <CarouselItem className='relative w-[20%] basis-1/5' key={indexImg}>
                                                        <img className='w-full h-full object-cover aspect-square rounded' id={`preview-image-${indexImg}`} alt='' />
                                                        <button
                                                            type='button'
                                                            onClick={() => handleRomoveImg(indexImg)}
                                                            className='absolute top-1 left-5 bg-black bg-opacity-80 rounded-full p-1 shadow-lg'
                                                        >
                                                            <XIcon className='size-3 text-white' />
                                                        </button>
                                                    </CarouselItem>
                                                ))
                                            }
                                        </CarouselContent>
                                    </Carousel>
                                </>
                            )}
                        />
                    </div>
                </div>
                <div className='mt-[1rem] w-1/2 mx-auto gap-10 lg:mt-[1.5rem] flex mb-5'>
                    <Button onClick={() => hideModal()} type='button' className='w-1/2' variant='outline'>{t('button.cancel')}</Button>
                    <Button
                        className='w-1/2'
                        onClick={form.handleSubmit(onSubmit)} // Sử dụng handleSubmit để validate trước khi gọi onSubmit
                    >
                        {t('button.save')}
                    </Button>
                </div>
            </form>
        </Form>
    );
});
